// src/utils/accessMatrixDiff.js
//
// Diffs two `system/accessControl` matrices into an audit-log payload: which
// page / collection keys changed, and which roles were added or removed on each.
// A whole-document snapshot of a 42-page, 67-collection matrix tells a reader
// nothing about what a given save actually did.
//
// ⚠️ THE WRITE IS A MERGE, AND A MERGE NEVER DELETES.
// `permissionsStore.savePermissions()` writes with `setDoc(..., { merge: true })`.
// A key that is absent from the payload is RETAINED in Firestore, not removed —
// verified against the Firestore emulator on 2026-08-20 (probe + positive control
// using FieldValue.delete(), which does remove it).
//
// So this function reports the EFFECTIVE change — what the write really did — and
// puts keys the caller dropped into `notRemovedByMerge` instead of counting them
// as removals. An audit log that claims a removal Firestore never performed is
// worse than no audit log: it is a record that disagrees with the database.

// Cap on how many changed keys get itemised. A save normally touches one or two;
// the cap exists so a bulk edit cannot write a multi-megabyte event. Anything
// dropped is COUNTED in the summary — a silent truncation would read as "that is
// all that changed".
const MAX_ITEMISED_KEYS = 40

function asRoles(value) {
  return Array.isArray(value) ? value.filter(r => typeof r === 'string') : []
}

function rolesDelta(beforeRoles, afterRoles) {
  const b = asRoles(beforeRoles)
  const a = asRoles(afterRoles)
  return {
    added: a.filter(r => !b.includes(r)),
    removed: b.filter(r => !a.includes(r)),
  }
}

// `pages[key]` is a role array. `collections[key]` is a map of role arrays
// ({ read, write, fullVisibility, ... }) — iterate whatever sub-keys are present
// on either side rather than assuming read/write, or a change to a third list
// goes unlogged.
function collectionSubKeys(beforeEntry, afterEntry) {
  return [
    ...new Set([
      ...Object.keys(beforeEntry && typeof beforeEntry === 'object' ? beforeEntry : {}),
      ...Object.keys(afterEntry && typeof afterEntry === 'object' ? afterEntry : {}),
    ]),
  ].sort()
}

/**
 * @param {object} before  the matrix as it stands in Firestore right now
 * @param {object} after   the matrix about to be written (the merge payload)
 * @returns {{fields: string[], delta: object[], before: object, after: object,
 *           summary: object, notRemovedByMerge: string[], truncated: boolean}}
 */
export function diffAccessMatrix(before = {}, after = {}) {
  const changed = [] // { key, added, removed, beforeRoles, afterRoles }
  const notRemovedByMerge = []

  for (const half of ['pages', 'collections']) {
    const b = (before && before[half]) || {}
    const a = (after && after[half]) || {}

    for (const key of [...new Set([...Object.keys(b), ...Object.keys(a)])].sort()) {
      const inAfter = Object.prototype.hasOwnProperty.call(a, key)

      // Absent from the payload ⇒ the merge leaves it exactly as it was.
      if (!inAfter) {
        notRemovedByMerge.push(`${half}.${key}`)
        continue
      }

      if (half === 'pages') {
        const d = rolesDelta(b[key], a[key])
        if (d.added.length || d.removed.length || !Object.prototype.hasOwnProperty.call(b, key)) {
          changed.push({
            key: `pages.${key}`,
            ...d,
            beforeRoles: asRoles(b[key]),
            afterRoles: asRoles(a[key]),
            isNew: !Object.prototype.hasOwnProperty.call(b, key),
          })
        }
        continue
      }

      for (const sub of collectionSubKeys(b[key], a[key])) {
        const d = rolesDelta(b[key]?.[sub], a[key]?.[sub])
        // An explicit empty permission key is still a saved structural change.
        // Otherwise new deny-all collections would be written with no audit.
        const isNewPermission =
          Object.prototype.hasOwnProperty.call(a[key] || {}, sub) &&
          !Object.prototype.hasOwnProperty.call(b[key] || {}, sub)
        if (d.added.length || d.removed.length || isNewPermission) {
          changed.push({
            key: `collections.${key}.${sub}`,
            ...d,
            beforeRoles: asRoles(b[key]?.[sub]),
            afterRoles: asRoles(a[key]?.[sub]),
            isNew: !Object.prototype.hasOwnProperty.call(b, key),
          })
        }
      }
    }
  }

  const itemised = changed.slice(0, MAX_ITEMISED_KEYS)
  const beforeMap = {}
  const afterMap = {}
  for (const c of itemised) {
    beforeMap[c.key] = c.beforeRoles
    afterMap[c.key] = c.afterRoles
  }

  return {
    fields: itemised.map(c => c.key),
    delta: itemised.map(c => ({
      key: c.key,
      added: c.added,
      removed: c.removed,
      ...(c.isNew ? { isNew: true } : {}),
    })),
    before: beforeMap,
    after: afterMap,
    summary: {
      keysChanged: changed.length,
      keysItemised: itemised.length,
      keysOmitted: changed.length - itemised.length,
      pagesChanged: changed.filter(c => c.key.startsWith('pages.')).length,
      collectionsChanged: changed.filter(c => c.key.startsWith('collections.')).length,
      rolesAdded: changed.reduce((n, c) => n + c.added.length, 0),
      rolesRemoved: changed.reduce((n, c) => n + c.removed.length, 0),
      keysRetainedByMerge: notRemovedByMerge.length,
    },
    // Capped for the same reason as `delta`; the count above is the truth.
    notRemovedByMerge: notRemovedByMerge.slice(0, MAX_ITEMISED_KEYS),
    truncated: changed.length > itemised.length,
  }
}

/** True when the write would change nothing a reader would care about. */
export function isNoOpDiff(diff) {
  return diff.summary.keysChanged === 0 && diff.summary.keysRetainedByMerge === 0
}
